import { lazy } from "react";
import { Navigate, Outlet, useRoutes } from "react-router-dom";
import ForgotPassword from "./pages/auth/ForgotPassword";
import TeamSetup from "./pages/auth/TeamSetup";
import SelectTeam from "./pages/auth/SelectTeam";
import Dashboard from "./pages/dashboard/Dashboard";
import PageNotFound from "./pages/pageNotFound";
import ContactUs from "./pages/ContactUs";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Successful from "./pages/Successful";
import Settings from "./pages/dashboard/Settings";
import Explore from "./pages/dashboard/Explore";
import MailSent from "./pages/auth/MailSent";
import PlanningBoard from "./pages/dashboard/PlanningBoard";
import NewFete from "pages/dashboard/NewFete";
import Prompts from "pages/prompts";
import Favourite from "pages/dashboard/Favourite";
import Vendor from "components/Vendor";

// Vendor specific imports
import VendorLogin from "components/vendor/Loginv";
import VendorOtp from "components/vendor/Otpv";
import Onboarding1 from "components/vendor/Onboarding1";
import Onboarding2 from "components/vendor/Onboarding2";
import Onboarding3 from "components/vendor/Onboarding3";
import Onboarding4 from "components/vendor/Onboarding4";
import Onboarding5 from "components/vendor/Onboarding5";
import Onboarding6 from "components/vendor/Onboarding6";
import Onboarding7 from "components/vendor/Onboarding7";

// Lazy-loaded components
const Login = lazy(() => import("./pages/auth/Login"));
const SignUp = lazy(() => import("./pages/auth/SignUp"));
const VerifyUserScreen = lazy(() => import("./pages/auth/VerifyUserScreen"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));

// Helper function to get logged-in user type
const getUserType = () => {
  const userData = localStorage.getItem("userData");
  const vendorData = localStorage.getItem("vendorData");

  try {
    if (userData && JSON.parse(userData).accessToken) {
      return "user";
    } else if (vendorData && JSON.parse(vendorData).accessToken) {
      return "vendor";
    }
  } catch (error) {
    console.error("Error parsing user data:", error);
    return null; // Fallback to null if there's a problem with parsing.
  }

  return null; // Return null if no valid user or vendor token exists.
};

// Route Configurations
const Routing = () => {
  const userType = getUserType();

  // console.log({ userType })

  const routes = [
    // Public routes, accessible by anyone (no auth required)
    {
      path: "/login",
      element: userType ? <Navigate to="/" /> : <Login />,
    },
    {
      path: "/signup",
      element: userType ? <Navigate to="/" /> : <SignUp />,
    },
    {
      path: "/forgotpassword",
      element: userType ? <Navigate to="/" /> : <ForgotPassword />,
    },
    {
      path: "/verifyuser",
      element: userType ? <Navigate to="/" /> : <VerifyUserScreen />,
    },
    {
      path: "/reset-password",
      element: userType ? <Navigate to="/" /> : <ResetPassword />,
    },
    {
      path: "/privacy-policy",
      element: <PrivacyPolicy />,
    },
    {
      path: "/contact-us",
      element: <ContactUs />,
    },
    {
      path: "/vendor-upload",
      element: <Vendor />,
    },

    // User routes, accessible only to users
    {
      path: "/",
      element: userType === "user" ? <Outlet /> : !userType ? <Navigate to="/login" /> : <Navigate to="/vendor/onboarding1" />,
      children: [
        { path: "/", element: <Dashboard /> },
        { path: "/fete/:id", element: <PlanningBoard /> },
        { path: "/team-setup", element: <TeamSetup /> },
        { path: "/select-team", element: <SelectTeam /> },
        { path: "/successful", element: <Successful /> },
        { path: "/mail-sent", element: <MailSent /> },
        { path: "/settings", element: <Settings /> },
        { path: "/explore", element: <Explore /> },
        { path: "/favourite", element: <Favourite /> },
        { path: "/fete/new/:draftEventId", element: <NewFete /> },
        { path: "/prompt-store", element: <Prompts /> },
      ],
    },

    // Vendor routes, accessible only to vendors
    {
      path: "/vendor/login",
      element: userType ? <Navigate to="/vendor/onboarding1" /> : <VendorLogin />,
    },
    {
      path: "/vendor/otp",
      element: userType ? <Navigate to="/vendor/onboarding1" /> : <VendorOtp />,
    },
    {
      path: "/vendor",
      element: userType === "vendor" ? <Outlet /> : !userType ? <Navigate to="/vendor/login" /> : <Navigate to="/" />,
      children: [
        { path: "", element: <Onboarding1 /> },
        { path: "onboarding1", element: <Onboarding1 /> },
        { path: "onboarding2", element: <Onboarding2 /> },
        { path: "onboarding3", element: <Onboarding3 /> },
        { path: "onboarding4", element: <Onboarding4 /> },
        { path: "onboarding5", element: <Onboarding5 /> },
        { path: "onboarding6", element: <Onboarding6 /> },
        { path: "onboarding7", element: <Onboarding7 /> }
      ],
    },

    // Catch-all for unknown routes
    { path: "*", element: <PageNotFound /> },
  ];

  return useRoutes(routes);
};

export default Routing;
